import {
  getCalendarFeedEvents,
  type CalendarFeedFilterType,
  type FeedEvent,
} from "@/lib/calendar-feed-api";
import type { CalendarEvent, CalendarEventType } from "@/components/calendar/calendar-types";
import type { Blockout } from "@/types";

/**
 * Appointments rebuild 86-03: THE mapping from a unified-feed event to the
 * flat `CalendarEvent` every view, widget and phone screen consumes. The
 * calendar page and the dashboard adapter used to carry one each, and they
 * disagreed on the id: one emitted the composite feed id, the other the raw
 * appointment uuid — which is why `event.source?.id ?? event.id` existed.
 *
 * One shape now: `id` is the composite feed id (unique across appointment,
 * travel-arrival and travel-departure rows born from the same record — the
 * React key and the drag matcher), and `source` is always present with the
 * RAW id the API routes expect.
 */
export interface MapFeedEventOptions {
  /** Fail-closed: the visit badge is dropped when visit categorization is off. */
  visitEnabled: boolean;
  /** Fail-closed: the room badge is dropped when rooms are off. */
  roomsEnabled: boolean;
}

const EVERYTHING: MapFeedEventOptions = { visitEnabled: true, roomsEnabled: true };

/**
 * Dentour 261009 — which visit an ARRIVAL leg is for the patient, bucketed
 * 1 / 2 / 3 / 4+ exactly as the booking board buckets its arrival planes. The
 * feed only sends `visit_ordinal` when the clinic is on
 * `appointments.calendar.travel_visit_colors`, so this is null on every other
 * clinic — and on departures, which stay orange as they do on the board.
 */
export function arrivalVisitBucket(isArrival: boolean, ordinal: unknown): 1 | 2 | 3 | 4 | null {
  if (!isArrival || typeof ordinal !== "number") return null;
  if (ordinal <= 1) return 1;
  if (ordinal === 2) return 2;
  if (ordinal === 3) return 3;
  return 4;
}

/**
 * Solid grid-chip fill per visit bucket. Dentour 261015: white for a first
 * visit, the arrival green the chip already had for the second, brown
 * (amber-900) for a returning patient — third and later share it. The legend
 * draws its swatches from the same map. A white fill needs dark text and an
 * edge, which `getEventStyle` adds (see `whiteTravelChip`).
 */
export const ARRIVAL_VISIT_FILL: Record<1 | 2 | 3 | 4, string> = {
  1: "#ffffff",
  2: "#22c55e",
  3: "#78350f",
  4: "#78350f",
};

/**
 * ALL-DAY travel events keep their LEGACY visual placement: an all-day travel
 * marker is pinned to a 09:00–10:00 local display slot (exactly like the
 * pre-feed path did with `new Date(dateStr + 'T09:00:00')`), so day/week/month/
 * doctors/rooms render identically until the all-day lane owns them. A TIMED
 * leg (`all_day: false` — the trip carries a flight time, Smile 260922) keeps
 * the feed's real start/end and lands on its hour in the grid. `eventType`
 * preserves the arrival/departure split the existing filters + event-style.ts
 * key on; `allDay` tells the lane and the month view which kind they hold.
 */
export function mapFeedEventToCalendarEvent(
  fe: FeedEvent,
  opts: MapFeedEventOptions = EVERYTHING
): CalendarEvent {
  const meta = fe.meta ?? {};
  const isTravel = fe.type === "travel_arrival" || fe.type === "travel_departure";

  const isVisit = fe.type === "visit";

  let start: Date;
  let end: Date;
  if ((isTravel || isVisit) && fe.all_day) {
    // Legacy parity: pin the all-day marker to a 09:00–10:00 slot on its date.
    const dateStr = fe.start.slice(0, 10);
    start = new Date(`${dateStr}T09:00:00`);
    end = new Date(`${dateStr}T10:00:00`);
  } else {
    start = new Date(fe.start);
    end = new Date(fe.end);
  }

  const eventType: CalendarEventType =
    fe.type === "travel_arrival"
      ? "arrival"
      : fe.type === "travel_departure"
        ? "departure"
        : isVisit ? "visit" : "appointment";

  // Appointment INTENT for getEventStyle; null for travel/blockout (legacy rows
  // are already treated as clinic_visit downstream).
  const intent =
    fe.type === "clinic_visit" || fe.type === "lead_call" || fe.type === "internal"
      ? fe.type
      : null;

  // The feed sends a semantic color_token (e.g. "event.clinic"), not CSS.
  // Chips fall back to `backgroundColor: event.color` whenever the event-types
  // styling flag is off, so emit the legacy hex here to keep that path valid.
  const visitBucket = arrivalVisitBucket(fe.type === "travel_arrival", meta.visit_ordinal);
  const legacyColor =
    visitBucket !== null
      ? ARRIVAL_VISIT_FILL[visitBucket]
      : fe.type === "travel_arrival"
        ? "#22c55e"
        : fe.type === "travel_departure"
          ? "#f97316"
          : isVisit ? "#8b5cf6" : meta.category === "klea"
            ? "#ec4899"
            : meta.category === "other"
              ? "#3b82f6"
              : "#6b7280";

  return {
    id: fe.id,
    title: fe.title,
    color: legacyColor,
    start,
    end,
    lead: fe.subject?.lead ?? undefined,
    user: fe.resources?.user ?? undefined,
    coDoctors: fe.resources?.co_doctors ?? undefined,
    status: (fe.status as CalendarEvent["status"]) ?? "scheduled",
    category: meta.category as string | undefined,
    type: intent,
    eventType,
    allDay: fe.all_day,
    time_arrived: (meta.time_arrived as string | null | undefined) ?? null,
    time_seated: (meta.time_seated as string | null | undefined) ?? null,
    time_dismissed: (meta.time_dismissed as string | null | undefined) ?? null,
    clinical_visit_id: (meta.clinical_visit_id as string | null | undefined) ?? null,
    visit: opts.visitEnabled
      ? ((meta.visit as CalendarEvent["visit"]) ?? null)
      : null,
    room: opts.roomsEnabled ? (fe.resources?.room ?? null) : null,
    source: fe.source,
    feedMeta: fe.meta,
  };
}

/**
 * A unified-feed blockout row. Extends the shared Blockout with the rooms-view
 * `room_id` (which the base Blockout type doesn't declare) so a feed blockout
 * satisfies BOTH the blockout dialog (Blockout) and the Calendar `blockouts`
 * prop (BlockoutWindow).
 */
export type FeedBlockout = Blockout & { room_id?: string | null };

/** Map a unified-feed blockout event → the BlockoutWindow the views shade. */
export function mapFeedBlockout(fe: FeedEvent): FeedBlockout {
  const meta = fe.meta ?? {};
  return {
    id: fe.source.id,
    user_id: (meta.user_id as number | undefined) ?? fe.resources?.user?.id ?? 0,
    user: fe.resources?.user ?? undefined,
    starts_at: fe.start,
    ends_at: fe.end,
    reason:
      (meta.reason as string | null | undefined) ??
      (meta.label as string | null | undefined) ??
      null,
    created_at: "",
    updated_at: "",
    room_id: (meta.room_id as string | null | undefined) ?? fe.resources?.room?.id ?? null,
  };
}

/**
 * The appointment-shaped feed types. Callers that carry a per-role posture
 * (the mobile app's useCalendarPosture) narrow this further; every dashboard
 * widget wants all three.
 */
const DEFAULT_APPOINTMENT_TYPES: CalendarFeedFilterType[] = [
  "clinic_visit",
  "lead_call",
  "internal",
];

/**
 * Appointment events for a date range, served by the unified calendar feed —
 * the adapter the dashboard widgets and the phone's Today screen consume.
 * Appointment sources only, mapped by the ONE mapper above.
 */
export const getCalendarEvents = async (
  start: string,
  end: string,
  userId?: string,
  category?: string,
  types: CalendarFeedFilterType[] = DEFAULT_APPOINTMENT_TYPES,
  opts: MapFeedEventOptions = EVERYTHING
): Promise<{ data: CalendarEvent[]; count: number }> => {
  const response = await getCalendarFeedEvents({
    start,
    end,
    types,
    userId,
    category,
  });

  const data = response.data
    .filter((fe) => fe.source.type === "appointment")
    .map((fe) => mapFeedEventToCalendarEvent(fe, opts));

  return { data, count: data.length };
};
